import type { FC, ReactNode } from 'react';
import { Fragment } from 'react';

import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import cn from 'classnames';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  containerClassName?: string;
  position?: 'center' | 'top' | 'bottom';
  initialFocus?: React.RefObject<HTMLElement | null>;
  afterLeave?: () => void;
  // Skip the enter transition on first mount so the dialog is visible on first paint.
  disableInitialTransition?: boolean;
  zIndexClassName?: string;
}

const popPanelTransition = {
  enterFrom: 'opacity-0 scale-95',
  enterTo: 'opacity-100 scale-100',
  leaveFrom: 'opacity-100 scale-100',
  leaveTo: 'opacity-0 scale-95',
} as const;

const sheetPanelTransition = {
  enterFrom: 'translate-y-full',
  enterTo: 'transform-none',
  leaveFrom: 'transform-none',
  leaveTo: 'translate-y-full',
} as const;

export const Dialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  children,
  className,
  backdropClassName = 'bg-black/60',
  containerClassName,
  position = 'center',
  initialFocus,
  afterLeave,
  disableInitialTransition = false,
  zIndexClassName = 'z-50',
}) => {
  return (
    <Transition appear={!disableInitialTransition} show={isOpen} as={Fragment} afterLeave={afterLeave}>
      <HeadlessDialog
        as="div"
        className={cn('relative', zIndexClassName)}
        onClose={onClose}
        initialFocus={initialFocus}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={cn('fixed inset-0', backdropClassName)} />
        </Transition.Child>

        {/* Positioning container */}
        <div
          className={cn(
            'fixed flex justify-center',
            zIndexClassName,
            {
              'inset-0 items-center p-4': position === 'center',
              'inset-x-0 top-16 items-start': position === 'top',
              'inset-x-0 bottom-0': position === 'bottom',
            },
            containerClassName,
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            leave="ease-in duration-150"
            {...(position === 'bottom' ? sheetPanelTransition : popPanelTransition)}
          >
            <HeadlessDialog.Panel className={className}>{children}</HeadlessDialog.Panel>
          </Transition.Child>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};
